import React, { useState, useEffect } from 'react';
import { X, Upload, Eye, Calendar, Tag, Folder, Image as ImageIcon, FileText, Video, Grid, MessageCircle, User, Clock, Lock, Globe, Search, Code, Type, Link, Hash, CheckCircle, XCircle, List, BarChart2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { getImageUrl } from '../../utils/imageUtils';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const UltimateBlogEditor = ({ post, categories, onClose, onSave }) => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  
  // Handle author field - extract name from object if needed
  const getAuthorName = (author) => {
    if (!author) return 'Admin';
    if (typeof author === 'string') return author;
    if (typeof author === 'object' && author.name) return author.name;
    return 'Admin';
  };

  const [formData, setFormData] = useState({
    title: post?.title || '',
    content_html: post?.content_html || '',
    excerpt: post?.excerpt || '',
    category: post?.category || '',
    featured_image: post?.featured_image || post?.cover_image || '',
    status: post?.status || 'draft',
    featured: post?.featured || false,
    tags: post?.tags || [],
    post_format: post?.post_format || 'standard',
    slug: post?.slug || '',
    meta_title: post?.meta_title || '',
    meta_description: post?.meta_description || '',
    meta_keywords: post?.meta_keywords || '',
    visibility: post?.visibility || 'public',
    password: post?.password || '',
    scheduled_date: post?.scheduled_date || '',
    scheduled_time: post?.scheduled_time || '',
    allow_comments: post?.allow_comments !== false,
    allow_pingbacks: post?.allow_pingbacks !== false,
    author: getAuthorName(post?.author),
    custom_fields: post?.custom_fields || [],
    canonical_url: post?.canonical_url || '',
    robots_index: post?.robots_index !== false,
    robots_follow: post?.robots_follow !== false,
  });

  const [tagInput, setTagInput] = useState('');
  const [imagePreview, setImagePreview] = useState(post?.featured_image || post?.cover_image || '');
  const [editorMode, setEditorMode] = useState('visual'); // 'visual' or 'code'
  const editorRef = React.useRef(null);
  const [focusKeyword, setFocusKeyword] = useState('');
  const [seoScore, setSeoScore] = useState(0);
  const [seoAnalysis, setSeoAnalysis] = useState({
    basic: [],
    readability: [],
    advanced: []
  });
  
  // Image upload states
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [cropPosition, setCropPosition] = useState({ x: 0.5, y: 0.5 });
  const [originalImageDimensions, setOriginalImageDimensions] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageScale, setImageScale] = useState(1.0); // 0.5 to 2.0 scale factor

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'featured_image') setImagePreview(value);
  };

  const handleImageFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Invalid File Type",
          description: "Please select a valid image file (JPG, PNG, GIF, WEBP)",
          variant: "destructive"
        });
        return;
      }

      // Validate file size (max 10MB for processing)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "File size must be less than 10MB",
          variant: "destructive"
        });
        return;
      }

      // Set file and show local preview immediately
      setSelectedImageFile(file);
      setUploadProgress({ status: 'uploading', message: 'Preparing upload...' });
      
      // Create local preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        
        // Get original image dimensions
        const img = new window.Image();
        img.onload = () => {
          setOriginalImageDimensions({ width: img.width, height: img.height });
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
      
      // Auto-upload the image immediately after selection
      uploadImageFile(file);
    }
  };
  
  // Separate upload function that can be called with the file directly
  const uploadImageFile = async (file) => {
    if (!file) return;
    
    setUploadingImage(true);
    setUploadProgress({ status: 'uploading', message: 'Uploading image...' });

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      // Use proportional mode by default - maintains aspect ratio, no cropping
      formDataUpload.append('mode', 'proportional');
      formDataUpload.append('max_width', '1200');
      formDataUpload.append('max_height', '800');

      setUploadProgress({ status: 'processing', message: 'Optimizing image...' });

      const response = await fetch(`${BACKEND_URL}/api/upload/blog-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        const newImageUrl = data.url;
        
        // Immediately update both formData and preview
        setFormData(prev => ({ ...prev, featured_image: newImageUrl }));
        setImagePreview(newImageUrl);
        setSelectedImageFile(null);
        
        // Show success with optimization details including final dimensions
        const sizeReduction = data.size_reduction_percent || 0;
        const optimizedKB = Math.round(data.optimized_size / 1024);
        const dims = data.dimensions || {};
        
        setUploadProgress({ 
          status: 'success', 
          message: `✓ Image uploaded (${dims.width}x${dims.height}, ${optimizedKB}KB, ${sizeReduction}% smaller)`,
          details: data
        });
        
        toast({
          title: "Success",
          description: `Image uploaded and optimized! (${data.dimensions.width}x${data.dimensions.height})`,
        });
      } else {
        const errorData = await response.json();
        setUploadProgress({ status: 'error', message: errorData.detail || 'Upload failed' });
        toast({
          title: "Upload Failed",
          description: errorData.detail || "Failed to upload image",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('[BLOG UPLOAD] Error:', error);
      setUploadProgress({ status: 'error', message: 'Connection error' });
      toast({
        title: "Error",
        description: "Error uploading image",
        variant: "destructive"
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const addTag = (tag) => {
    const cleanTag = tag.trim().toLowerCase().replace(/^#/, '');
    if (cleanTag && !formData.tags.includes(cleanTag)) {
      handleChange('tags', [...formData.tags, cleanTag]);
    }
    setTagInput('');
  };

  const removeTag = (tagToRemove) => {
    handleChange('tags', formData.tags.filter(t => t !== tagToRemove));
  };

  const handleSave = async () => {
    if (!formData.title || !formData.content_html) {
      toast({
        title: "Validation Error",
        description: "Title and content are required",
        variant: "destructive"
      });
      return;
    }

    if (!formData.slug) {
      const autoSlug = formData.title.toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-');
      formData.slug = autoSlug;
    }

    try {
      setSaving(true);
      await onSave(formData);
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save post",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const popularTags = ['fitness', 'nutrition', 'workout', 'health', 'training', 'muscle', 'cardio', 'diet', 'wellness', 'exercise'];

  // Initialize editor content when switching to visual mode
  useEffect(() => {
    if (editorRef.current && editorMode === 'visual') {
      // Only update if content is different (e.g., when switching from code mode)
      if (editorRef.current.innerHTML !== formData.content_html) {
        editorRef.current.innerHTML = formData.content_html;
      }
    }
  }, [editorMode]); // Only run when mode changes

  // Formatting functions for Visual Editor
  const applyFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      handleChange('content_html', editorRef.current.innerHTML);
    }
  };

  const insertHTML = (html) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertHTML', false, html);
      handleChange('content_html', editorRef.current.innerHTML);
    }
  };

  // SEO Analysis Function
  const analyzeSEO = () => {
    if (!focusKeyword) {
      setSeoScore(0);
      setSeoAnalysis({ basic: [], readability: [], advanced: [] });
      return;
    }

    const keyword = focusKeyword.toLowerCase();
    const title = formData.title.toLowerCase();
    const content = formData.content_html.toLowerCase().replace(/<[^>]*>/g, '');
    const metaDesc = formData.meta_description.toLowerCase();
    const slug = formData.slug.toLowerCase();
    
    // Word count
    const words = content.split(/\s+/).filter(w => w.length > 0);
    const wordCount = words.length;
    
    // Keyword density
    const keywordCount = (content.match(new RegExp(keyword, 'g')) || []).length;
    const keywordDensity = ((keywordCount / wordCount) * 100).toFixed(2);
    
    // Check if keyword is in first 10% of content
    const first10Percent = content.substring(0, content.length * 0.1);
    const keywordInIntro = first10Percent.includes(keyword);
    
    const analysis = {
      basic: [],
      readability: [],
      advanced: []
    };
    
    let score = 0;
    
    // BASIC SEO CHECKS
    
    // 1. SEO Title contains keyword
    if (formData.meta_title && formData.meta_title.toLowerCase().includes(keyword)) {
      analysis.basic.push({ status: 'success', text: `✓ SEO başlığı odak anahtar kelimeyi içeriyor: "${focusKeyword}"` });
      score += 10;
    } else {
      analysis.basic.push({ status: 'error', text: `✗ SEO başlığı odak anahtar kelimeyi içermiyor: "${focusKeyword}"` });
    }
    
    // 2. Meta description contains keyword
    if (metaDesc.includes(keyword)) {
      analysis.basic.push({ status: 'success', text: `✓ Meta açıklama odak anahtar kelimeyi içeriyor` });
      score += 10;
    } else {
      analysis.basic.push({ status: 'error', text: `✗ Meta açıklama odak anahtar kelimeyi içermiyor` });
    }
    
    // 3. URL contains keyword
    if (slug.includes(keyword.replace(/\s+/g, '-'))) {
      analysis.basic.push({ status: 'success', text: `✓ URL odak anahtar kelimeyi içeriyor` });
      score += 10;
    } else {
      analysis.basic.push({ status: 'warning', text: `⚠ URL odak anahtar kelimeyi içermiyor` });
    }
    
    // 4. Keyword in first 10%
    if (keywordInIntro) {
      analysis.basic.push({ status: 'success', text: `✓ Odak anahtar kelime içeriğin ilk %10'unda kullanılmış` });
      score += 10;
    } else {
      analysis.basic.push({ status: 'warning', text: `⚠ Odak anahtar kelime içeriğin ilk %10'unda kullanılmamış` });
    }
    
    // 5. Keyword density
    if (keywordDensity >= 0.5 && keywordDensity <= 2.5) {
      analysis.basic.push({ status: 'success', text: `✓ İçerikte ${keywordCount} kez odak anahtar kelime kullanılmış (yoğunluk: ${keywordDensity}%)` });
      score += 10;
    } else if (keywordDensity < 0.5) {
      analysis.basic.push({ status: 'warning', text: `⚠ Odak anahtar kelime çok az kullanılmış: ${keywordCount} kez (${keywordDensity}%)` });
    } else {
      analysis.basic.push({ status: 'error', text: `✗ Odak anahtar kelime çok fazla kullanılmış: ${keywordCount} kez (${keywordDensity}%)` });
    }
    
    // READABILITY CHECKS
    
    // 6. Content length
    if (wordCount >= 600) {
      analysis.readability.push({ status: 'success', text: `✓ İçerik ${wordCount} kelime (ideal: 600+)` });
      score += 15;
    } else if (wordCount >= 300) {
      analysis.readability.push({ status: 'warning', text: `⚠ İçerik ${wordCount} kelime (önerilen: 600+)` });
      score += 8;
    } else {
      analysis.readability.push({ status: 'error', text: `✗ İçerik çok kısa: ${wordCount} kelime (minimum: 300)` });
    }
    
    // 7. Title length
    const titleLength = formData.title.length;
    if (titleLength >= 30 && titleLength <= 60) {
      analysis.readability.push({ status: 'success', text: `✓ Başlık uzunluğu ideal: ${titleLength} karakter` });
      score += 10;
    } else if (titleLength < 30) {
      analysis.readability.push({ status: 'warning', text: `⚠ Başlık çok kısa: ${titleLength} karakter (önerilen: 30-60)` });
      score += 5;
    } else {
      analysis.readability.push({ status: 'error', text: `✗ Başlık çok uzun: ${titleLength} karakter (önerilen: 30-60)` });
    }
    
    // 8. Meta description length
    const metaLength = formData.meta_description.length;
    if (metaLength >= 120 && metaLength <= 160) {
      analysis.readability.push({ status: 'success', text: `✓ Meta açıklama uzunluğu ideal: ${metaLength} karakter` });
      score += 10;
    } else if (metaLength < 120) {
      analysis.readability.push({ status: 'warning', text: `⚠ Meta açıklama çok kısa: ${metaLength} karakter (önerilen: 120-160)` });
      score += 5;
    } else {
      analysis.readability.push({ status: 'error', text: `✗ Meta açıklama çok uzun: ${metaLength} karakter (önerilen: 120-160)` });
    }
    
    // ADVANCED CHECKS
    
    // 9. Images
    const imageCount = (formData.content_html.match(/<img/g) || []).length;
    if (imageCount > 0) {
      analysis.advanced.push({ status: 'success', text: `✓ İçerikte ${imageCount} görsel bulunuyor` });
      score += 5;
    } else {
      analysis.advanced.push({ status: 'warning', text: `⚠ İçerikte görsel bulunmuyor` });
    }
    
    // 10. Links
    const linkCount = (formData.content_html.match(/<a /g) || []).length;
    if (linkCount > 0) {
      analysis.advanced.push({ status: 'success', text: `✓ İçerikte ${linkCount} bağlantı var` });
      score += 5;
    } else {
      analysis.advanced.push({ status: 'warning', text: `⚠ İçerikte bağlantı yok` });
    }
    
    // 11. Headings
    const headingCount = (formData.content_html.match(/<h[2-6]/g) || []).length;
    if (headingCount >= 2) {
      analysis.advanced.push({ status: 'success', text: `✓ İçerikte ${headingCount} başlık etiketi kullanılmış` });
      score += 5;
    } else {
      analysis.advanced.push({ status: 'warning', text: `⚠ İçerikte yeterli başlık etiketi yok (${headingCount})` });
    }
    
    // 12. Featured image
    if (formData.featured_image) {
      analysis.advanced.push({ status: 'success', text: `✓ Öne çıkan görsel ayarlanmış` });
      score += 5;
    } else {
      analysis.advanced.push({ status: 'warning', text: `⚠ Öne çıkan görsel ayarlanmamış` });
    }
    
    setSeoScore(Math.min(score, 100));
    setSeoAnalysis(analysis);
  };

  // Run SEO analysis when content changes
  useEffect(() => {
    if (focusKeyword) {
      analyzeSEO();
    }
  }, [formData.title, formData.content_html, formData.meta_title, formData.meta_description, formData.slug, focusKeyword]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4 overflow-hidden">
      <div className="bg-white dark:bg-gray-800 rounded-none sm:rounded-lg shadow-2xl w-full h-full sm:h-[95vh] sm:max-w-[1600px] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-black text-white px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-none sm:rounded-t-lg">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold">Ultimate Blog Editor</h2>
            <p className="text-xs sm:text-sm opacity-90 hidden sm:block">WordPress-style comprehensive post editor</p>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white dark:bg-gray-800 hover:bg-opacity-20 rounded-full p-2">
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Main Content - Single scroll area on mobile, split on desktop */}
        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col lg:flex-row min-h-full">
            {/* Left Panel - Content */}
            <div className="flex-1 p-4 sm:p-6 space-y-4 sm:space-y-6 lg:overflow-y-auto">
            {/* Title */}
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Add title"
              className="w-full text-2xl sm:text-3xl lg:text-4xl font-bold border-0 focus:ring-0 focus:outline-none placeholder-gray-300 dark:placeholder-gray-500 px-0 bg-transparent dark:text-white"
            />

            {/* Permalink */}
            <div className="bg-yellow-50 dark:bg-gray-700 border-l-4 border-yellow-400 dark:border-yellow-600 p-4 rounded">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Link className="w-4 h-4" />
                  Permalink
                </span>
                <button
                  onClick={() => {
                    const slug = formData.title.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
                    handleChange('slug', slug);
                  }}
                  className="text-xs bg-yellow-200 dark:bg-yellow-600 hover:bg-yellow-300 dark:hover:bg-yellow-700 px-3 py-1 rounded text-yellow-900 dark:text-white font-medium"
                >
                  Generate
                </button>
              </div>
              <div className="flex items-center gap-1 text-sm">
                <span className="text-gray-500 dark:text-gray-400">{window.location.origin}/blog/post/</span>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => handleChange('slug', e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                  className="flex-1 px-2 py-1 border border-yellow-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 dark:text-white text-sm"
                  placeholder="post-url-slug"
                />
              </div>
            </div>

            {/* Content Editor with Visual/Code Tabs */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Content</label>
                <span className="text-xs text-gray-500 dark:text-gray-400">{formData.content_html.length} characters</span>
              </div>
              
              {/* Editor Tabs */}
              <div className="border-2 border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                {/* Tab Headers */}
                <div className="flex bg-gray-100 dark:bg-gray-700 border-b-2 border-gray-300 dark:border-gray-600">
                  <button
                    type="button"
                    onClick={() => setEditorMode('visual')}
                    className={`px-6 py-2 text-sm font-medium transition-colors ${
                      editorMode === 'visual'
                        ? 'bg-white dark:bg-gray-800 text-red-600 border-b-2 border-purple-600 -mb-0.5 z-10'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    📝 Visual Editor
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditorMode('code')}
                    className={`px-6 py-2 text-sm font-medium transition-colors ${
                      editorMode === 'code'
                        ? 'bg-white dark:bg-gray-800 text-red-600 border-b-2 border-purple-600 -mb-0.5 z-10'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    💻 HTML Code
                  </button>
                </div>

                {/* Visual Editor Mode */}
                {editorMode === 'visual' && (
                  <div className="bg-white dark:bg-gray-800">
                    {/* Formatting Toolbar */}
                    <div className="flex flex-wrap gap-1 p-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-300 dark:border-gray-600 overflow-x-auto">
                      {/* Heading Dropdown */}
                      <select
                        onChange={(e) => applyFormat('formatBlock', e.target.value)}
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600"
                        defaultValue=""
                      >
                        <option value="">Paragraph</option>
                        <option value="h1">Heading 1</option>
                        <option value="h2">Heading 2</option>
                        <option value="h3">Heading 3</option>
                        <option value="h4">Heading 4</option>
                      </select>

                      <div className="w-px h-8 bg-gray-300 mx-1" />

                      {/* Bold */}
                      <button
                        type="button"
                        onClick={() => applyFormat('bold')}
                        className="px-3 py-1 border border border-gray-300 dark:border-gray-600 rounded text-sm font-bold bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Bold (Ctrl+B)"
                      >
                        B
                      </button>

                      {/* Italic */}
                      <button
                        type="button"
                        onClick={() => applyFormat('italic')}
                        className="px-3 py-1 border border border-gray-300 dark:border-gray-600 rounded text-sm italic bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Italic (Ctrl+I)"
                      >
                        I
                      </button>

                      {/* Underline */}
                      <button
                        type="button"
                        onClick={() => applyFormat('underline')}
                        className="px-3 py-1 border border border-gray-300 dark:border-gray-600 rounded text-sm underline bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Underline (Ctrl+U)"
                      >
                        U
                      </button>

                      {/* Strikethrough */}
                      <button
                        type="button"
                        onClick={() => applyFormat('strikeThrough')}
                        className="px-3 py-1 border border border-gray-300 dark:border-gray-600 rounded text-sm line-through bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Strikethrough"
                      >
                        S
                      </button>

                      <div className="w-px h-8 bg-gray-300 mx-1" />

                      {/* Bullet List */}
                      <button
                        type="button"
                        onClick={() => applyFormat('insertUnorderedList')}
                        className="px-3 py-1 border border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Bullet List"
                      >
                        • List
                      </button>

                      {/* Numbered List */}
                      <button
                        type="button"
                        onClick={() => applyFormat('insertOrderedList')}
                        className="px-3 py-1 border border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Numbered List"
                      >
                        1. List
                      </button>

                      <div className="w-px h-8 bg-gray-300 mx-1" />

                      {/* Link */}
                      <button
                        type="button"
                        onClick={() => {
                          const url = prompt('Enter URL:');
                          if (url) applyFormat('createLink', url);
                        }}
                        className="px-3 py-1 border border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Insert Link"
                      >
                        🔗 Link
                      </button>

                      {/* Image */}
                      <button
                        type="button"
                        onClick={() => {
                          const url = prompt('Enter image URL:');
                          if (url) insertHTML(`<img src="${url}" alt="Image" style="max-width: 100%; height: auto;" />`);
                        }}
                        className="px-3 py-1 border border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Insert Image"
                      >
                        🖼️ Image
                      </button>

                      <div className="w-px h-8 bg-gray-300 mx-1" />

                      {/* Align Left */}
                      <button
                        type="button"
                        onClick={() => applyFormat('justifyLeft')}
                        className="px-3 py-1 border border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Align Left"
                      >
                        ⫷
                      </button>

                      {/* Align Center */}
                      <button
                        type="button"
                        onClick={() => applyFormat('justifyCenter')}
                        className="px-3 py-1 border border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Align Center"
                      >
                        ⫸
                      </button>

                      {/* Align Right */}
                      <button
                        type="button"
                        onClick={() => applyFormat('justifyRight')}
                        className="px-3 py-1 border border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Align Right"
                      >
                        ⫹
                      </button>

                      <div className="w-px h-8 bg-gray-300 mx-1" />

                      {/* Remove Formatting */}
                      <button
                        type="button"
                        onClick={() => applyFormat('removeFormat')}
                        className="px-3 py-1 border border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Clear Formatting"
                      >
                        🧹 Clear
                      </button>
                    </div>

                    {/* Visual Editor Area */}
                    <div
                      ref={editorRef}
                      contentEditable
                      suppressContentEditableWarning
                      onInput={(e) => {
                        handleChange('content_html', e.currentTarget.innerHTML);
                      }}
                      onBlur={(e) => {
                        handleChange('content_html', e.currentTarget.innerHTML);
                      }}
                      className="min-h-[300px] sm:min-h-[400px] lg:min-h-[500px] p-3 sm:p-4 prose dark:prose-invert max-w-none focus:outline-none focus:ring-2 focus:ring-red-500 text-sm sm:text-base bg-white dark:bg-gray-800 dark:text-white"
                      style={{ minHeight: '300px' }}
                    />
                  </div>
                )}

                {/* Code Editor Mode */}
                {editorMode === 'code' && (
                  <div className="bg-white dark:bg-gray-800">
                    <textarea
                      value={formData.content_html}
                      onChange={(e) => handleChange('content_html', e.target.value)}
                      rows={20}
                      className="w-full px-4 py-3 border-0 focus:ring-2 focus:ring-red-500 font-mono text-sm bg-white dark:bg-gray-800 dark:text-white"
                      placeholder="Write your HTML code here..."
                    />
                  </div>
                )}
              </div>

              {editorMode === 'code' && (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-gray-700 p-3 rounded">
                  <p className="font-semibold mb-1 dark:text-white">💡 Quick HTML Tips:</p>
                  <ul className="space-y-0.5 ml-4 list-disc">
                    <li><code>&lt;h2&gt;Heading&lt;/h2&gt;</code> for headings</li>
                    <li><code>&lt;p&gt;Paragraph&lt;/p&gt;</code> for paragraphs</li>
                    <li><code>&lt;strong&gt;Bold&lt;/strong&gt;</code> or <code>&lt;b&gt;Bold&lt;/b&gt;</code></li>
                    <li><code>&lt;em&gt;Italic&lt;/em&gt;</code> or <code>&lt;i&gt;Italic&lt;/i&gt;</code></li>
                    <li><code>&lt;a href="url"&gt;Link Text&lt;/a&gt;</code> for links</li>
                    <li><code>&lt;img src="url" alt="description" /&gt;</code> for images</li>
                  </ul>
                </div>
              )}
            </div>

            {/* Excerpt */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Excerpt</label>
              <textarea
                value={formData.excerpt}
                onChange={(e) => handleChange('excerpt', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 dark:text-white"
                placeholder="Write an excerpt for listings and SEO..."
              />
            </div>

            {/* Custom Fields */}
            <div className="border-2 border-dashed border border-gray-300 dark:border-gray-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  Custom Fields
                </h3>
                <button
                  onClick={() => handleChange('custom_fields', [...formData.custom_fields, { key: '', value: '' }])}
                  className="text-xs bg-purple-100 hover:bg-purple-200 px-3 py-1 rounded text-red-700 font-medium"
                >
                  + Add Field
                </button>
              </div>
              <div className="space-y-2">
                {formData.custom_fields.map((field, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      type="text"
                      value={field.key}
                      onChange={(e) => {
                        const fields = [...formData.custom_fields];
                        fields[i].key = e.target.value;
                        handleChange('custom_fields', fields);
                      }}
                      placeholder="Key"
                      className="flex-1 px-3 py-2 border border border-gray-300 dark:border-gray-600 rounded text-sm"
                    />
                    <input
                      type="text"
                      value={field.value}
                      onChange={(e) => {
                        const fields = [...formData.custom_fields];
                        fields[i].value = e.target.value;
                        handleChange('custom_fields', fields);
                      }}
                      placeholder="Value"
                      className="flex-1 px-3 py-2 border border border-gray-300 dark:border-gray-600 rounded text-sm"
                    />
                    <button
                      onClick={() => handleChange('custom_fields', formData.custom_fields.filter((_, idx) => idx !== i))}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-gray-600 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {formData.custom_fields.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-4">No custom fields. Click "+ Add Field" to create one.</p>
                )}
              </div>
            </div>
          </div>

          {/* Right Sidebar - Mobile: Full width below content, Desktop: Fixed width sidebar */}
          <div className="w-full lg:w-96 bg-gray-50 dark:bg-gray-700 border-t-2 lg:border-t-0 lg:border-l-2 border-gray-200 dark:border-gray-700 lg:overflow-y-auto">
            <div className="p-4 space-y-4">
              
              {/* Publish */}
              <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg p-4 shadow-sm">
                <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-red-600" />
                  Publish
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => handleChange('status', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white rounded-lg text-sm font-medium"
                    >
                      <option value="draft">📝 Draft</option>
                      <option value="published">✅ Published</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 p-2 hover:bg-red-50 dark:hover:bg-gray-600 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => handleChange('featured', e.target.checked)}
                      className="w-4 h-4 text-red-600 rounded"
                    />
                    <span className="text-sm font-medium dark:text-white">⭐ Mark as Featured</span>
                  </label>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full py-3 bg-gradient-to-r from-red-600 to-black text-white rounded-lg font-bold hover:from-red-700 hover:to-gray-900 disabled:opacity-50"
                  >
                    {saving ? '⏳ Saving...' : '💾 Save Post'}
                  </button>
                </div>
              </div>

              {/* Visibility */}
              <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg p-4 shadow-sm">
                <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-red-600" />
                  Visibility
                </h3>
                <div className="space-y-2">
                  {[
                    { value: 'public', label: '🌐 Public', icon: Globe },
                    { value: 'private', label: '🔒 Private', icon: Lock },
                    { value: 'password', label: '🔑 Password', icon: Lock }
                  ].map(vis => (
                    <label key={vis.value} className="flex items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-600 rounded cursor-pointer">
                      <input
                        type="radio"
                        name="visibility"
                        value={vis.value}
                        checked={formData.visibility === vis.value}
                        onChange={(e) => handleChange('visibility', e.target.value)}
                        className="w-4 h-4 text-red-600"
                      />
                      <span className="ml-3 text-sm font-medium dark:text-white">{vis.label}</span>
                    </label>
                  ))}
                  {formData.visibility === 'password' && (
                    <input
                      type="text"
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      placeholder="Enter password"
                      className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white rounded text-sm mt-2"
                    />
                  )}
                </div>
              </div>

              {/* Schedule */}
              <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg p-4 shadow-sm">
                <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-red-600" />
                  Schedule
                </h3>
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">📅 Date</label>
                    <input
                      type="date"
                      value={formData.scheduled_date}
                      onChange={(e) => handleChange('scheduled_date', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">⏰ Time</label>
                    <input
                      type="time"
                      value={formData.scheduled_time}
                      onChange={(e) => handleChange('scheduled_time', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white rounded text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Categories */}
              <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg p-4 shadow-sm">
                <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Folder className="w-5 h-5 text-red-600" />
                  Category
                </h3>
                <select
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white rounded-lg text-sm font-medium"
                >
                  <option value="">Select category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.slug}>
                      {cat.name} ({cat.post_count || 0})
                    </option>
                  ))}
                </select>
              </div>

              {/* Tags */}
              <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg p-4 shadow-sm">
                <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Tag className="w-5 h-5 text-red-600" />
                  Tags
                </h3>
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      addTag(tagInput);
                    }
                  }}
                  placeholder="Add tags (comma/enter)"
                  className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white rounded text-sm mb-2"
                />
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.tags.map(tag => (
                      <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-red-700 rounded-full text-xs font-medium">
                        #{tag}
                        <button onClick={() => removeTag(tag)} className="hover:text-purple-900">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Popular:</div>
                <div className="flex flex-wrap gap-1">
                  {popularTags.filter(t => !formData.tags.includes(t)).map(tag => (
                    <button
                      key={tag}
                      onClick={() => addTag(tag)}
                      className="px-2 py-1 text-xs bg-gray-100 hover:bg-purple-100 text-gray-700 dark:text-gray-300 hover:text-red-700 rounded"
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Featured Image */}
              <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg p-4 shadow-sm">
                <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-red-600" />
                  Featured Image
                </h3>
                
                {/* Image Preview */}
                {imagePreview && !showCropModal && (
                  <div className="relative mb-3 bg-gray-50 dark:bg-gray-700 border-2 border-dashed border dark:border-gray-700-purple-300 rounded-lg p-2">
                    <p className="text-xs font-semibold text-red-700 mb-2">Preview (1200x630):</p>
                    <img 
                      key={`preview-${imagePreview}`}
                      src={imagePreview.startsWith('data:') || imagePreview.startsWith('blob:') ? imagePreview : getImageUrl(imagePreview)} 
                      alt="Preview" 
                      className="w-full h-40 object-cover rounded border-2 border-gray-200 dark:border-gray-700" 
                      onError={(e) => {
                        console.error('Image preview load failed:', imagePreview);
                        setImagePreview(null);
                      }}
                    />
                    <button 
                      type="button"
                      onClick={() => { 
                        handleChange('featured_image', ''); 
                        setImagePreview(null);
                        setSelectedImageFile(null);
                        setUploadProgress(null);
                      }} 
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Upload Progress */}
                {uploadProgress && (
                  <div className={`mb-3 p-3 rounded-lg text-sm ${
                    uploadProgress.status === 'success' ? 'bg-green-50 border border-green-200 text-green-700' :
                    uploadProgress.status === 'error' ? 'bg-red-50 border border-red-200 text-red-700' :
                    'bg-blue-50 border border-blue-200 text-blue-700'
                  }`}>
                    {uploadProgress.status === 'uploading' && (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                        {uploadProgress.message}
                      </div>
                    )}
                    {uploadProgress.status === 'processing' && (
                      <div className="flex items-center gap-2">
                        <div className="animate-pulse">🔄</div>
                        {uploadProgress.message}
                      </div>
                    )}
                    {uploadProgress.status === 'success' && uploadProgress.message}
                    {uploadProgress.status === 'error' && `❌ ${uploadProgress.message}`}
                  </div>
                )}
                
                {/* File Input */}
                <div className="mb-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileSelect}
                    className="w-full text-sm text-gray-600 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-red-600 file:text-white hover:file:bg-purple-700 file:cursor-pointer cursor-pointer"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">📸 JPG, PNG, GIF, WEBP (Max 10MB)</p>
                  <p className="text-xs text-green-600 mt-1">✨ Automatically optimized to 1200x630</p>
                </div>

                {/* Current Image URL Display (Read-only) */}
                {formData.featured_image && !selectedImageFile && (
                  <div className="mt-2 p-2 bg-gray-100 rounded text-xs">
                    <p className="text-gray-600 dark:text-gray-400 mb-1">Current URL:</p>
                    <p className="text-gray-800 font-mono break-all">{formData.featured_image}</p>
                  </div>
                )}
              </div>

              {/* Image Crop Modal */}
              {showCropModal && selectedImageFile && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
                    <div className="bg-gradient-to-r from-red-600 to-black text-white px-4 py-3 flex items-center justify-between">
                      <h3 className="font-bold">📷 Image Crop & Optimization</h3>
                      <button 
                        onClick={() => {
                          setShowCropModal(false);
                          setSelectedImageFile(null);
                          setImagePreview(formData.featured_image || null);
                        }}
                        className="text-white hover:bg-white/20 rounded-full p-1"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    
                    <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
                      {/* Original Image Info */}
                      {originalImageDimensions && (
                        <div className="mb-3 p-2 bg-blue-50 dark:bg-gray-700 rounded text-sm">
                          <span className="font-semibold">Original Size:</span> {originalImageDimensions.width} x {originalImageDimensions.height}px
                          <span className="mx-2">|</span>
                          <span className="font-semibold">File:</span> {(selectedImageFile.size / 1024).toFixed(1)}KB
                        </div>
                      )}
                      
                      {/* Preview with Crop Overlay */}
                      <div className="relative mb-4 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700">
                        <div className="relative flex items-center justify-center" style={{ paddingBottom: '52.5%' }}> {/* 1200:630 aspect ratio */}
                          {/* Background pattern for transparency */}
                          <div className="absolute inset-0 opacity-10" style={{
                            backgroundImage: 'repeating-conic-gradient(#808080 0% 25%, transparent 0% 50%)',
                            backgroundSize: '20px 20px'
                          }}></div>
                          {/* Image with zoom - when zoomed out, show entire image */}
                          <img 
                            src={imagePreview} 
                            alt="Crop Preview"
                            className="absolute"
                            style={{
                              maxWidth: `${imageScale * 100}%`,
                              maxHeight: `${imageScale * 100}%`,
                              width: imageScale <= 1 ? `${imageScale * 100}%` : 'auto',
                              height: imageScale <= 1 ? 'auto' : `${imageScale * 100}%`,
                              objectFit: 'contain',
                              left: `${cropPosition.x * (100 - imageScale * 100)}%`,
                              top: `${cropPosition.y * (100 - imageScale * 100)}%`,
                            }}
                          />
                          <div className="absolute inset-0 border-4 border-dashed border-white/50 pointer-events-none"></div>
                          <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                            1200 x 630
                          </div>
                          {imageScale < 1 && (
                            <div className="absolute top-2 left-2 bg-yellow-500/80 text-black text-xs px-2 py-1 rounded font-semibold">
                              Fit to Frame ({Math.round(imageScale * 100)}%)
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Zoom/Scale Control */}
                      <div className="mb-4 p-3 bg-yellow-50 dark:bg-gray-700 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          🔍 Zoom / Scale: {Math.round(imageScale * 100)}%
                          {imageScale < 1 && <span className="ml-2 text-yellow-600">(Fit entire image)</span>}
                          {imageScale > 1 && <span className="ml-2 text-blue-600">(Zoom in - crop)</span>}
                        </label>
                        <input 
                          type="range" 
                          min="50" 
                          max="200" 
                          value={imageScale * 100}
                          onChange={(e) => setImageScale(e.target.value / 100)}
                          className="w-full h-2 bg-yellow-200 rounded-lg appearance-none cursor-pointer accent-yellow-600"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>50% (Zoom Out)</span>
                          <span>100%</span>
                          <span>200% (Zoom In)</span>
                        </div>
                        <div className="flex gap-2 mt-2">
                          <button
                            type="button"
                            onClick={() => setImageScale(0.5)}
                            className="px-2 py-1 text-xs bg-yellow-100 hover:bg-yellow-200 rounded"
                          >
                            50%
                          </button>
                          <button
                            type="button"
                            onClick={() => setImageScale(0.75)}
                            className="px-2 py-1 text-xs bg-yellow-100 hover:bg-yellow-200 rounded"
                          >
                            75%
                          </button>
                          <button
                            type="button"
                            onClick={() => setImageScale(1.0)}
                            className="px-2 py-1 text-xs bg-yellow-100 hover:bg-yellow-200 rounded"
                          >
                            100%
                          </button>
                          <button
                            type="button"
                            onClick={() => setImageScale(1.5)}
                            className="px-2 py-1 text-xs bg-yellow-100 hover:bg-yellow-200 rounded"
                          >
                            150%
                          </button>
                          <button
                            type="button"
                            onClick={() => setImageScale(2.0)}
                            className="px-2 py-1 text-xs bg-yellow-100 hover:bg-yellow-200 rounded"
                          >
                            200%
                          </button>
                        </div>
                      </div>
                      
                      {/* Crop Position Controls */}
                      <div className="space-y-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            ↔️ Horizontal Position: {Math.round(cropPosition.x * 100)}%
                          </label>
                          <input 
                            type="range" 
                            min="0" 
                            max="100" 
                            value={cropPosition.x * 100}
                            onChange={(e) => setCropPosition(prev => ({ ...prev, x: e.target.value / 100 }))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>Left</span>
                            <span>Center</span>
                            <span>Right</span>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            ↕️ Vertical Position: {Math.round(cropPosition.y * 100)}%
                          </label>
                          <input 
                            type="range" 
                            min="0" 
                            max="100" 
                            value={cropPosition.y * 100}
                            onChange={(e) => setCropPosition(prev => ({ ...prev, y: e.target.value / 100 }))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>Top</span>
                            <span>Center</span>
                            <span>Bottom</span>
                          </div>
                        </div>
                        
                        {/* Quick Position Buttons */}
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => setCropPosition({ x: 0.5, y: 0.5 })}
                            className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                          >
                            📍 Center
                          </button>
                          <button
                            type="button"
                            onClick={() => setCropPosition({ x: 0.5, y: 0 })}
                            className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                          >
                            ⬆️ Top
                          </button>
                          <button
                            type="button"
                            onClick={() => setCropPosition({ x: 0.5, y: 1 })}
                            className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                          >
                            ⬇️ Bottom
                          </button>
                          <button
                            type="button"
                            onClick={() => setCropPosition({ x: 0, y: 0.5 })}
                            className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                          >
                            ⬅️ Left
                          </button>
                          <button
                            type="button"
                            onClick={() => setCropPosition({ x: 1, y: 0.5 })}
                            className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                          >
                            ➡️ Right
                          </button>
                        </div>
                      </div>
                      
                      {/* Info Box */}
                      <div className="p-3 bg-green-50 dark:bg-gray-700 border border-green-200 dark:border-green-700 rounded-lg text-sm text-green-800 dark:text-green-300 mb-4">
                        <p className="font-semibold mb-1">✨ Automatic Optimization</p>
                        <ul className="text-xs space-y-1 ml-4 list-disc">
                          <li>Image will be scaled to 1200x630</li>
                          <li>Optimized for web performance</li>
                          <li>File size will be automatically reduced</li>
                        </ul>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowCropModal(false);
                          setSelectedImageFile(null);
                          setImagePreview(formData.featured_image || null);
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleUploadImage}
                        disabled={uploadingImage}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-red-600 to-black text-white rounded-lg font-semibold hover:from-red-700 hover:to-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {uploadingImage ? (
                          <>
                            <Upload className="w-4 h-4 inline mr-2 animate-bounce" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 inline mr-2" />
                            Upload & Optimize
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Post Format */}
              <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg p-4 shadow-sm">
                <h3 className="font-bold text-gray-900 dark:text-white mb-3">Format</h3>
                <div className="space-y-2">
                  {[
                    { value: 'standard', label: '📝 Standard', icon: FileText },
                    { value: 'gallery', label: '🎨 Gallery', icon: Grid },
                    { value: 'video', label: '🎥 Video', icon: Video }
                  ].map(format => (
                    <label key={format.value} className={`flex items-center p-2 border-2 rounded cursor-pointer ${formData.post_format === format.value ? 'border-purple-600 bg-red-50' : 'border-gray-200 dark:border-gray-700 hover:border-red-300'}`}>
                      <input type="radio" name="post_format" value={format.value} checked={formData.post_format === format.value} onChange={(e) => handleChange('post_format', e.target.value)} className="w-4 h-4 text-red-600" />
                      <span className="ml-3 text-sm font-medium">{format.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Discussion */}
              <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg p-4 shadow-sm">
                <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-red-600" />
                  Discussion
                </h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-600 rounded cursor-pointer">
                    <input type="checkbox" checked={formData.allow_comments} onChange={(e) => handleChange('allow_comments', e.target.checked)} className="w-4 h-4 text-red-600 rounded" />
                    <span className="text-sm">💬 Allow comments</span>
                  </label>
                  <label className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-600 rounded cursor-pointer">
                    <input type="checkbox" checked={formData.allow_pingbacks} onChange={(e) => handleChange('allow_pingbacks', e.target.checked)} className="w-4 h-4 text-red-600 rounded" />
                    <span className="text-sm">📡 Allow pingbacks</span>
                  </label>
                </div>
              </div>

              {/* Author */}
              <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg p-4 shadow-sm">
                <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <User className="w-5 h-5 text-red-600" />
                  Author
                </h3>
                <input
                  type="text"
                  value={formData.author}
                  onChange={(e) => handleChange('author', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white rounded text-sm"
                  placeholder="Author name"
                />
              </div>

              {/* SEO Analysis - Rank Math Style */}
              <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg p-4 shadow-sm">
                <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Search className="w-5 h-5 text-red-600" />
                  SEO Analizi
                  {seoScore > 0 && (
                    <span className={`ml-auto px-2 py-1 rounded text-xs font-bold ${
                      seoScore >= 80 ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 
                      seoScore >= 50 ? 'bg-yellow-100 text-yellow-700' : 
                      'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                    }`}>
                      {seoScore}/100
                    </span>
                  )}
                </h3>
                
                {/* Focus Keyword */}
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">🎯 Odak Anahtar Kelime</label>
                  <input 
                    type="text" 
                    value={focusKeyword} 
                    onChange={(e) => setFocusKeyword(e.target.value)}
                    onBlur={analyzeSEO}
                    className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white rounded text-sm" 
                    placeholder="personal trainer toronto" 
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">SEO analizi için ana anahtar kelimenizi girin</p>
                </div>

                {/* Google Preview */}
                {focusKeyword && (
                  <div className="mb-4 p-3 border-2 border-gray-200 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700 dark:text-white">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">📱 Google Önizleme</div>
                    <div className="text-sm text-blue-600 mb-1 truncate">{formData.meta_title || formData.title}</div>
                    <div className="text-xs text-green-600 mb-1 truncate">{window.location.origin}/blog/post/{formData.slug}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{formData.meta_description || formData.excerpt}</div>
                  </div>
                )}

                {/* SEO Analysis Results */}
                {focusKeyword && seoAnalysis.basic.length > 0 && (
                  <div className="space-y-3 mb-4">
                    {/* Basic SEO */}
                    <div>
                      <div className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Temel SEO
                      </div>
                      <div className="space-y-1">
                        {seoAnalysis.basic.map((item, idx) => (
                          <div key={idx} className={`text-xs p-2 rounded ${
                            item.status === 'success' ? 'bg-green-50 text-green-700' :
                            item.status === 'warning' ? 'bg-yellow-50 text-yellow-700' :
                            'bg-red-50 text-red-700'
                          }`}>
                            {item.text}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Readability */}
                    <div>
                      <div className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Okunabilirlik
                      </div>
                      <div className="space-y-1">
                        {seoAnalysis.readability.map((item, idx) => (
                          <div key={idx} className={`text-xs p-2 rounded ${
                            item.status === 'success' ? 'bg-green-50 text-green-700' :
                            item.status === 'warning' ? 'bg-yellow-50 text-yellow-700' :
                            'bg-red-50 text-red-700'
                          }`}>
                            {item.text}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Advanced */}
                    <div>
                      <div className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                        <BarChart2 className="w-4 h-4" />
                        Gelişmiş
                      </div>
                      <div className="space-y-1">
                        {seoAnalysis.advanced.map((item, idx) => (
                          <div key={idx} className={`text-xs p-2 rounded ${
                            item.status === 'success' ? 'bg-green-50 text-green-700' :
                            item.status === 'warning' ? 'bg-yellow-50 text-yellow-700' :
                            'bg-red-50 text-red-700'
                          }`}>
                            {item.text}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* SEO Fields */}
                <div className="space-y-3 border-t-2 pt-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">SEO Başlığı</label>
                    <input type="text" value={formData.meta_title} onChange={(e) => handleChange('meta_title', e.target.value)} maxLength={60} className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white rounded text-sm" placeholder="SEO başlığı" />
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{formData.meta_title.length}/60</div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Meta Açıklama</label>
                    <textarea value={formData.meta_description} onChange={(e) => handleChange('meta_description', e.target.value)} maxLength={160} rows={2} className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white rounded text-sm" placeholder="Meta açıklama" />
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{formData.meta_description.length}/160</div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Meta Anahtar Kelimeler</label>
                    <input type="text" value={formData.meta_keywords} onChange={(e) => handleChange('meta_keywords', e.target.value)} className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white rounded text-sm" placeholder="kelime1, kelime2" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Canonical URL</label>
                    <input type="text" value={formData.canonical_url} onChange={(e) => handleChange('canonical_url', e.target.value)} className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white rounded text-sm" placeholder="https://..." />
                  </div>
                  <div className="space-y-1">
                    <label className="flex items-center gap-2 text-xs">
                      <input type="checkbox" checked={formData.robots_index} onChange={(e) => handleChange('robots_index', e.target.checked)} className="w-3 h-3 text-red-600 rounded" />
                      <span>🤖 Arama motorlarının indekslemesine izin ver</span>
                    </label>
                    <label className="flex items-center gap-2 text-xs">
                      <input type="checkbox" checked={formData.robots_follow} onChange={(e) => handleChange('robots_follow', e.target.checked)} className="w-3 h-3 text-red-600 rounded" />
                      <span>🔗 Arama motorlarının bağlantıları takip etmesine izin ver</span>
                    </label>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default UltimateBlogEditor;